import { Trans, t } from "@lingui/macro";
import cx from "classnames";
import { useCallback } from "react";
import { Link } from "react-router-dom";

import Footer from "components/Footer/Footer";
import AddressView from "components/AddressView/AddressView";

import { useCopyPortfolio } from "domain/copyTrading/useCopyTradingData";
import { unfollowTrader } from "domain/copyTrading/contracts";

import { TraderSettingsPanel } from "../components/TraderSettingsPanel";

import { useChainId } from "lib/chains";
import { usePendingTxns } from "lib/usePendingTxns";
import useWallet from "lib/wallets/useWallet";
import { useEthersSigner } from "lib/wallets/useEthersSigner";

import "../CopyTradingPage.scss";

const BUSDC_DECIMALS = 18;

function formatUsdc(wei: bigint): string {
  const n = Number(wei) / 10 ** BUSDC_DECIMALS;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
}

export function CopyPortfolioPage() {
  const { account } = useWallet();
  const { chainId } = useChainId();
  const signer = useEthersSigner({ chainId });
  const [, setPendingTxns] = usePendingTxns();

  const portfolioState = useCopyPortfolio(account ?? undefined);

  const handleUnfollow = useCallback(
    async (traderAddr: string) => {
      if (!signer) return;
      try {
        await unfollowTrader({ chainId, signer, traderAddress: traderAddr, setPendingTxns });
      } catch {
        // callContract shows error toast
      }
    },
    [chainId, signer, setPendingTxns]
  );

  if (!account) {
    return (
      <div className="page-layout">
        <div className="default-container CT-page">
          <div className="CT-empty-state">
            <div className="CT-empty-state__icon">🔗</div>
            <h3><Trans>Connect your wallet</Trans></h3>
            <p><Trans>Connect your wallet to view your copy trading portfolio.</Trans></p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (portfolioState.status === "loading") {
    return (
      <div className="page-layout">
        <div className="default-container CT-page">
          <div className="CT-loading">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="CT-skeleton-row" />)}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (portfolioState.status === "error") {
    return (
      <div className="page-layout">
        <div className="default-container CT-page">
          <div className="CT-error-banner">
            <Trans>Failed to load portfolio — please try again later.</Trans>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const { totalAllocation, positionCount, positions, traderProfile } = portfolioState.data;

  return (
    <div className="page-layout">
      <div className="default-container CT-page">

        {/* Header */}
        <div className="CT-header">
          <div className="CT-header__title">
            <h1 className="CT-header__heading">
              <Trans>My Copy Portfolio</Trans>
            </h1>
            <p className="CT-header__sub">
              <Trans>Manage the traders you follow and your allocated capital.</Trans>
            </p>
          </div>
          <Link to="/copytrading" className="CT-btn-secondary">
            <Trans>← Leaderboard</Trans>
          </Link>
        </div>

        {/* Summary */}
        <div className="CT-hero CT-hero--compact">
          <div className="CT-hero-card CT-hero-card--blue">
            <div className="CT-hero-card__label"><Trans>Following</Trans></div>
            <div className="CT-hero-card__value">{positionCount}</div>
            <div className="CT-hero-card__sub"><Trans>traders</Trans></div>
          </div>
          <div className="CT-hero-card CT-hero-card--green">
            <div className="CT-hero-card__label"><Trans>Total Allocated</Trans></div>
            <div className="CT-hero-card__value">{formatUsdc(totalAllocation)}</div>
            <div className="CT-hero-card__sub">BUSDC</div>
          </div>
        </div>

        {/* Leader dashboard — shown when the connected wallet is a registered trader */}
        {traderProfile && account && (
          <TraderSettingsPanel account={account} profile={traderProfile} />
        )}

        {/* Positions */}
        {positions.length === 0 ? (
          <div className="CT-empty-state">
            <div className="CT-empty-state__icon">📈</div>
            <h3><Trans>No positions yet</Trans></h3>
            <p><Trans>Start following traders to mirror their positions automatically.</Trans></p>
            <Link to="/copytrading" className="CT-btn-primary">
              <Trans>Browse Traders</Trans>
            </Link>
          </div>
        ) : (
          <div className="CT-portfolio-grid">
            {positions.map((pos) => {
              const allocPct =
                totalAllocation > 0n
                  ? Math.round((Number(pos.settings.currentAllocation) / Number(totalAllocation)) * 100)
                  : 0;
              return (
                <div key={pos.trader} className={cx("CT-portfolio-card", { "CT-portfolio-card--paused": pos.settings.isPaused })}>
                  <div className="CT-portfolio-card__header">
                    <Link to={`/copytrading/trader/${pos.trader}`} className="CT-portfolio-card__trader">
                      <AddressView address={pos.trader} size={20} breakpoint="M" />
                    </Link>
                    {pos.settings.isPaused && (
                      <span className="CT-badge CT-badge--orange"><Trans>Paused</Trans></span>
                    )}
                  </div>

                  <div className="CT-portfolio-card__stats">
                    <div className="CT-portfolio-stat">
                      <span className="CT-portfolio-stat__label"><Trans>Per trade</Trans></span>
                      <span className="CT-portfolio-stat__value">{formatUsdc(pos.settings.collateralPerTrade)}</span>
                    </div>
                    <div className="CT-portfolio-stat">
                      <span className="CT-portfolio-stat__label"><Trans>Allocated</Trans></span>
                      <span className="CT-portfolio-stat__value">{formatUsdc(pos.settings.currentAllocation)}</span>
                    </div>
                    <div className="CT-portfolio-stat">
                      <span className="CT-portfolio-stat__label"><Trans>Max cap</Trans></span>
                      <span className="CT-portfolio-stat__value">
                        {pos.settings.maxTotalAllocation === 0n
                          ? t`Unlimited`
                          : formatUsdc(pos.settings.maxTotalAllocation)}
                      </span>
                    </div>
                    <div className="CT-portfolio-stat">
                      <span className="CT-portfolio-stat__label"><Trans>Share</Trans></span>
                      <span className="CT-portfolio-stat__value">{allocPct}%</span>
                    </div>
                  </div>

                  {/* Allocation bar */}
                  <div className="CT-allocation-bar">
                    <div
                      className="CT-allocation-bar__fill"
                      style={{ width: `${Math.min(100, allocPct)}%` }}
                    />
                  </div>

                  <button
                    className="CT-btn-unfollow"
                    onClick={() => handleUnfollow(pos.trader)}
                  >
                    <Trans>Unfollow</Trans>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
